// openInNewTab.ts — open a workspace's Code OSS in a browser tab of its own.
//
// The launch URL comes from an API call, and a browser only lets a page
// open a tab synchronously inside the click that asked for it: a
// window.open after the await is a popup, and is blocked. So the tab is
// opened blank first, inside the click, and pointed at the launch URL once
// the manager hands it back. If the call fails the blank tab is closed and
// the error goes to the caller.
//
// The tab is cut loose from this page (opener = null) before it navigates:
// it ends up on the code host, which serves whatever the agent's Pod
// returns, and that must not be able to steer the app's own tab. The
// launch URL is a one-time ticket the code host trades for a cookie
// scoped to /s/<workspace id>/ — the same exchange the framed editor
// makes, and it works the same at the top level.

/** What the manager's launch endpoint returns. */
export interface CodeLaunch {
  url: string;
}

/** The slice of a browser tab this module touches. */
export interface LaunchTab {
  opener: unknown;
  closed: boolean;
  close: () => void;
  location: { replace: (url: string) => void };
  document?: { title: string; body: { textContent: string | null } | null };
}

/** window.open, narrowed to the call this module makes. */
export type OpenTab = (url: string, target: string) => LaunchTab | null;

/** The browser refused to open the tab. */
export class PopupBlockedError extends Error {
  constructor() {
    super('the browser blocked the new tab; allow pop-ups for this site and try again');
    this.name = 'PopupBlockedError';
  }
}

/**
 * Open Code OSS in a new tab. Call it from the click handler itself, before
 * any await, so the tab opens as part of the user's gesture. Resolves once
 * the tab is on its way to the editor; rejects (with the tab closed) if
 * the browser blocked it or the launch failed.
 */
export async function openInNewTab(
  launch: () => Promise<CodeLaunch>,
  openTab: OpenTab = (url, target) => window.open(url, target),
): Promise<void> {
  const tab = openTab('', '_blank');
  if (!tab) throw new PopupBlockedError();
  tab.opener = null;
  try {
    if (tab.document) {
      tab.document.title = 'Opening VS Code…';
      if (tab.document.body) tab.document.body.textContent = 'Starting the workspace and opening VS Code…';
    }
  } catch {
    // Cosmetic only: a tab we cannot write into still navigates.
  }
  let res: CodeLaunch;
  try {
    res = await launch();
  } catch (err) {
    tab.close();
    throw err;
  }
  // The user closed the blank tab while waiting: they changed their mind.
  if (tab.closed) return;
  tab.location.replace(res.url);
}

/** A readable message from an axios error, a thrown Error, or anything else. */
export function launchErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  if (data?.error) return data.error;
  if (err instanceof Error) return err.message;
  return String(err);
}
